import { useCallback, useEffect, useRef, useState } from "react";

import { Alert, Box, CircularProgress, Stack, Typography } from "@mui/material";
import axios from "axios";
import { useParams } from "react-router";

import { orgSessionManager } from "@domains/auth/services/orgSessionManager";
import {
  VcsConnectionEndpoints,
  VcsConnectionSummary,
} from "@generated/vcs-connections";
import { apiBaseUrls } from "@platform/constants/shared-constants";

import { GitHubAppInstallCard, VcsActionButtons } from "./GitHubAppInstallCard";
import { IntegrationsSection } from "./IntegrationsSection";
import { VcsConnectionDialog } from "./VcsConnectionDialog";
import { VcsConnectionList } from "./VcsConnectionList";

const authHeaders = () => ({
  Authorization: `Bearer ${orgSessionManager.getToken() ?? ""}`,
});

// ── Main component ──────────────────────────────────────────────────────────

export default function VcsConnectionSetupPage() {
  const { orgAlias } = useParams<{ orgAlias: string }>();
  const [connections, setConnections] = useState<VcsConnectionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // GitHub App install flow state
  const [installing, setInstalling] = useState(false);
  const [installError, setInstallError] = useState<string | null>(null);
  const [polling, setPolling] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // GitLab OAuth flow state
  const [gitLabAuthing, setGitLabAuthing] = useState(false);
  const [gitLabAuthError, setGitLabAuthError] = useState<string | null>(null);

  // Delete confirmation dialog
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    if (!orgAlias) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get<VcsConnectionSummary[]>(
        `${apiBaseUrls.BACKEND}${VcsConnectionEndpoints.LIST}`,
        { headers: authHeaders() },
      );
      setConnections(res.data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load connections");
    } finally {
      setLoading(false);
    }
  }, [orgAlias]);

  useEffect(() => {
    void load();
  }, [load]);

  // Poll until a "connected" GitHub connection appears (up to 120s)
  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    setPolling(true);
    const deadline = Date.now() + 120_000;

    pollRef.current = setInterval(async () => {
      if (Date.now() > deadline) {
        clearInterval(pollRef.current!);
        pollRef.current = null;
        setPolling(false);
        setInstallError(
          "Connection not detected after 2 minutes. Refresh the page if the install completed.",
        );
        return;
      }
      try {
        const res = await axios.get<VcsConnectionSummary[]>(
          `${apiBaseUrls.BACKEND}${VcsConnectionEndpoints.LIST}`,
          { headers: authHeaders() },
        );
        const hasActive = res.data.some(
          (c) => c.provider === "github" && c.status === "connected",
        );
        if (hasActive) {
          setConnections(res.data);
          clearInterval(pollRef.current!);
          pollRef.current = null;
          setPolling(false);
          setInstalling(false);
        }
      } catch {
        // ignore transient errors during polling
      }
    }, 2000);
  }, []);

  // Stop polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, []);

  // Listen for postMessage + localStorage from the callback popup
  useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      if (e.data?.type === "GITHUB_INSTALL_SUCCESS") {
        startPolling();
      }
    };
    const handleStorage = (e: StorageEvent) => {
      if (e.key === "github-install-success" && e.newValue) {
        startPolling();
      }
    };
    window.addEventListener("message", handleMessage);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("message", handleMessage);
      window.removeEventListener("storage", handleStorage);
    };
  }, [startPolling]);

  const handleInstall = useCallback(async () => {
    setInstallError(null);
    setInstalling(true);
    try {
      const res = await axios.get<{ url: string }>(
        `${apiBaseUrls.BACKEND}/api/v1/github-app/install`,
        { headers: authHeaders() },
      );
      const popup = window.open(
        res.data.url,
        "github-app-install",
        "width=900,height=700,scrollbars=yes,resizable=yes",
      );
      if (!popup) {
        setInstallError(
          "Popup blocked. Allow popups for this site and try again.",
        );
        setInstalling(false);
        return;
      }
      // Start polling — the popup will postMessage on success via GitHubCallbackPage
      startPolling();
    } catch (e) {
      setInstallError(
        e instanceof Error ? e.message : "Failed to open GitHub installation",
      );
      setInstalling(false);
    }
  }, [startPolling]);

  const handleGitLabAuth = useCallback(async () => {
    setGitLabAuthError(null);
    setGitLabAuthing(true);
    try {
      const res = await axios.get<{ url: string; state: string }>(
        `${apiBaseUrls.BACKEND}/api/v1/vcs-connections/gitlab/auth-url`,
        { headers: authHeaders() },
      );
      const popup = window.open(
        res.data.url,
        "gitlab-oauth",
        "width=900,height=700,scrollbars=yes,resizable=yes",
      );
      if (!popup) {
        setGitLabAuthError(
          "Popup blocked. Allow popups for this site and try again.",
        );
        setGitLabAuthing(false);
        return;
      }
      // Store state for callback verification
      localStorage.setItem("gitlab-oauth-state", res.data.state);
      // Set up callback page to postMessage on success
      setGitLabAuthing(false);
    } catch (e) {
      setGitLabAuthError(
        e instanceof Error ? e.message : "Failed to get GitLab auth URL",
      );
      setGitLabAuthing(false);
    }
  }, []);

  const handleDeleteConfirm = async () => {
    if (deleteId === null) return;
    setDeleting(true);
    try {
      await axios.delete(
        `${apiBaseUrls.BACKEND}${VcsConnectionEndpoints.DELETE.replace(":id", String(deleteId))}`,
        { headers: authHeaders() },
      );
      setDeleteId(null);
      void load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to disconnect");
    } finally {
      setDeleting(false);
    }
  };

  const hasActiveGithub = connections.some(
    (c) => c.provider === "github" && c.status === "connected",
  );
  const hasActiveGitLab = connections.some(
    (c) => c.provider === "gitlab" && c.status === "connected",
  );
  const hasConnections = connections.length > 0;

  const actionButtonsProps = {
    installing,
    gitLabAuthing,
    polling,
    hasActiveGithub,
    hasActiveGitLab,
    onInstall: handleInstall,
    onGitLabAuth: handleGitLabAuth,
  };

  if (!orgAlias) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="text.secondary">
          Organization not found in URL.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 800 }}>
      {/* Header — only show action buttons when connections already exist */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h5" gutterBottom>
            VCS Connections
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Connect your GitHub or GitLab organization. Rusha uses this to
            provision repositories and receive push webhooks.
          </Typography>
        </Box>

        {hasConnections && (
          <VcsActionButtons {...actionButtonsProps} variant="header" />
        )}
      </Stack>

      {/* Errors */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {installError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setInstallError(null)}
        >
          {installError}
        </Alert>
      )}
      {gitLabAuthError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setGitLabAuthError(null)}
        >
          {gitLabAuthError}
        </Alert>
      )}

      {/* Polling banner */}
      {polling && (
        <Alert
          severity="info"
          icon={<CircularProgress size={16} />}
          sx={{ mb: 2 }}
        >
          Waiting for GitHub App installation to complete…
        </Alert>
      )}

      {/* GitLab OAuth banner */}
      {gitLabAuthing && (
        <Alert
          severity="info"
          icon={<CircularProgress size={16} />}
          sx={{ mb: 2 }}
        >
          Opening GitLab OAuth page…
        </Alert>
      )}

      {/* Connection list / empty state */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : !hasConnections ? (
        <GitHubAppInstallCard {...actionButtonsProps} />
      ) : (
        <VcsConnectionList
          connections={connections}
          loading={loading}
          installing={installing}
          gitLabAuthing={gitLabAuthing}
          polling={polling}
          hasActiveGithub={hasActiveGithub}
          hasActiveGitLab={hasActiveGitLab}
          onInstall={handleInstall}
          onGitLabAuth={handleGitLabAuth}
          onDisconnect={(id) => setDeleteId(id)}
        />
      )}

      {/* Delete confirmation dialog */}
      <VcsConnectionDialog
        open={deleteId !== null}
        deleting={deleting}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDeleteConfirm}
      />

      {/* ── Integrations Section ──────────────────────────────────────────────── */}
      <IntegrationsSection />
    </Box>
  );
}
